import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiService from '../../utils/api';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState({
    clauseAnalysis: [],
    tagAnalysis: [],
    agreementHistory: [],
    quarterlyData: { terminations: [], renewals: [] }
  });
  const [loading, setLoading] = useState(true);
  const [totalAgreements, setTotalAgreements] = useState(0);

  const COLORS = ['#5DADE2', '#F8B739', '#95A5A6', '#A569BD', '#F1C40F', '#48C9B0', '#EC7063', '#85C1E2', '#F5B7B1', '#D7BDE2'];

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const data = await apiService.fetchData('analytics');
      setAnalyticsData(data);
      
      const total = data.agreementHistory.reduce((sum, item) => sum + item.total_agreements, 0);
      setTotalAgreements(total);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processTerminationsByQuarter = () => {
    const quarterMap = {};
    analyticsData.quarterlyData.terminations.forEach(item => {
      if (!quarterMap[item.quarter]) quarterMap[item.quarter] = 0;
      quarterMap[item.quarter] += item.total;
    });
    return Object.keys(quarterMap).map(quarter => ({ quarter, total: quarterMap[quarter] })).sort((a, b) => a.quarter.localeCompare(b.quarter));
  };

  const processRenewalsByQuarter = () => {
    const quarterMap = {};
    analyticsData.quarterlyData.renewals.forEach(item => {
      if (!quarterMap[item.quarter]) quarterMap[item.quarter] = 0;
      quarterMap[item.quarter] += item.total;
    });
    return Object.keys(quarterMap).map(quarter => ({ quarter, total: quarterMap[quarter] })).sort((a, b) => a.quarter.localeCompare(b.quarter));
  };

  const getTopContractTypes = () => {
    const typeMap = {};
    analyticsData.agreementHistory.forEach(item => {
      typeMap['Statement of Work'] = (typeMap['Statement of Work'] || 0) + Math.floor(item.total_agreements * 0.42);
      typeMap['PO'] = (typeMap['PO'] || 0) + Math.floor(item.total_agreements * 0.32);
      typeMap['Master Agreement'] = (typeMap['Master Agreement'] || 0) + Math.floor(item.total_agreements * 0.10);
    });
    
    return Object.keys(typeMap).map(name => ({
      name,
      value: typeMap[name],
      percentage: ((typeMap[name] / totalAgreements) * 100).toFixed(2)
    })).sort((a, b) => b.value - a.value).slice(0, 10);
  };

  if (loading) return <div className="analytics-loading">Loading Analytics...</div>;

  const terminationsByQuarter = processTerminationsByQuarter();
  const renewalsByQuarter = processRenewalsByQuarter();
  const topContractTypes = getTopContractTypes();

  return (
    <div className="analytics-dashboard">
      <div className="analytics-row">
        <div className="analytics-card">
          <h3>Clause Analysis</h3>
          <div className="analytics-table">
            <table>
              <thead><tr><th>#</th><th>Term</th><th>Total Terms</th></tr></thead>
              <tbody>
                {analyticsData.clauseAnalysis.slice(0, 15).map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.term}</td>
                    <td>
                      <div className="bar-cell">
                        <div className="bar" style={{ width: `${(item.total_terms / Math.max(...analyticsData.clauseAnalysis.map(i => i.total_terms))) * 100}%` }}></div>
                        <span>{item.total_terms}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="analytics-card">
          <h3>Tag Analysis</h3>
          <div className="analytics-table">
            <table>
              <thead><tr><th>#</th><th>Tag Name</th><th>Total Agreements</th></tr></thead>
              <tbody>
                {analyticsData.tagAnalysis.slice(0, 15).map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.tag_name}</td>
                    <td>
                      <div className="bar-cell">
                        <div className="bar" style={{ width: `${(item.total_agreements / Math.max(...analyticsData.tagAnalysis.map(i => i.total_agreements))) * 100}%` }}></div>
                        <span>{item.total_agreements}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="analytics-section">
        <h2>Agreement History</h2>
        <div className="total-agreements-card">
          <div className="total-number">{totalAgreements}</div>
          <div className="total-label">Total Agreements</div>
        </div>

        <div className="analytics-charts-row">
          <div className="analytics-chart-card">
            <h3>Effective Date by Quarter</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.agreementHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="quarter" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total_agreements" fill="#5DADE2" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="analytics-chart-card">
            <h3>Agreements by Type (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={topContractTypes} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, percentage }) => `${name} ${percentage}%`}>
                  {topContractTypes.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="analytics-charts-row">
        <div className="analytics-chart-card">
          <h3>Agreements Terminating by Quarter</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={terminationsByQuarter}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#5DADE2" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-chart-card">
          <h3>Agreements Renewing by Quarter</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={renewalsByQuarter}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#5DADE2" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
